// apps/frontend/src/lib/pipeline-preview/transformers/transformation.ts
import type { PipelineNode, TransformationType, DataType } from '@/pages/pipelines/types/pipeline';
import type { ExecutionContext, TransformerResult } from '../types';
import { inferSchema } from '../utils/schema';

/**
 * Apply TRANSFORMATION node transformations to data
 */
export async function transformData(
  node: PipelineNode,
  inputData: unknown[],
  _context: ExecutionContext
): Promise<TransformerResult> {
  const startTime = performance.now();

  try {
    const config = node.data.config as any;
    if (!config?.transformations || !Array.isArray(config.transformations)) {
      return {
        success: false,
        data: [],
        schema: { columns: [] },
        stats: { rows: 0, executionTime: 0 },
        error: 'No transformations configured',
      };
    }

    let transformedData = [...inputData];

    // Apply each transformation sequentially
    for (const transformation of config.transformations) {
      transformedData = applyTransformation(transformedData, transformation);
    }

    // Infer schema from transformed data
    const schema = inferSchema(transformedData);

    const endTime = performance.now();
    return {
      success: true,
      data: transformedData,
      schema,
      stats: {
        rows: transformedData.length,
        executionTime: Math.round(endTime - startTime),
      },
    };
  } catch (error: any) {
    const endTime = performance.now();
    return {
      success: false,
      data: [],
      schema: { columns: [] },
      stats: { rows: 0, executionTime: Math.round(endTime - startTime) },
      error: error.message || 'Transformation failed',
    };
  }
}

/**
 * Apply a single transformation to all rows
 */
function applyTransformation(
  data: unknown[],
  transformation: {
    id: string;
    type: TransformationType;
    config: Record<string, any>
  }
): unknown[] {
  const { type, config } = transformation;

  switch (type as TransformationType) {
    case 'RENAME':
      return applyRename(data, config.mapping);
    case 'CAST':
      return applyCast(data, config.field, config.targetType);
    case 'SPLIT':
      return applySplit(data, config.field, config.separator, config.targetFields);
    case 'MERGE':
      return applyMerge(data, config.sourceFields, config.targetField, config.separator);
    case 'JSON_RULES':
      return applyJsonRules(data, config);
    case 'JAVASCRIPT':
      return applyJavaScript(data, config);
    default:
      return data;
  }
}

/**
 * RENAME: Change column names
 */
function applyRename(data: unknown[], mapping: Record<string, string>): unknown[] {
  return data.map(row => {
    const newRow: any = {};
    const oldRow = row as Record<string, unknown>;

    for (const [key, value] of Object.entries(oldRow)) {
      const newKey = mapping[key] || key;
      newRow[newKey] = value;
    }

    return newRow;
  });
}

/**
 * CAST: Convert data types
 */
function applyCast(data: unknown[], field: string, targetType: DataType): unknown[] {
  return data.map(row => {
    const newRow = { ...(row as Record<string, unknown>) };
    const value = newRow[field];

    if (value === null || value === undefined) {
      return newRow;
    }

    try {
      switch (targetType) {
        case 'STRING':
          newRow[field] = String(value);
          break;
        case 'NUMBER':
          newRow[field] = Number(value);
          break;
        case 'BOOLEAN':
          // Handle string boolean conversions explicitly
          if (typeof value === 'string') {
            const lower = value.toLowerCase().trim();
            if (lower === 'false' || lower === '0' || lower === '') {
              newRow[field] = false;
            } else if (lower === 'true' || lower === '1') {
              newRow[field] = true;
            } else {
              newRow[field] = Boolean(value); // fallback
            }
          } else {
            newRow[field] = Boolean(value);
          }
          break;
        case 'DATE':
          const date = new Date(value as any);
          if (isNaN(date.getTime())) {
            // Keep original value if date is invalid
            break;
          }
          newRow[field] = date;
          break;
        default:
          // Keep as-is
          break;
      }
    } catch (e) {
      console.warn(`[TRANSFORMATION] Failed to cast field "${field}" to ${targetType}:`, e);
      // Keep original value
    }

    return newRow;
  });
}

/**
 * SPLIT: Split string into multiple columns
 */
function applySplit(
  data: unknown[],
  field: string,
  separator: string,
  targetFields: string[]
): unknown[] {
  return data.map(row => {
    const newRow = { ...(row as Record<string, unknown>) };
    const value = newRow[field];

    if (typeof value === 'string') {
      const parts = value.split(separator);
      targetFields.forEach((targetField, index) => {
        newRow[targetField] = parts[index] || '';
      });
    }

    return newRow;
  });
}

/**
 * MERGE: Combine multiple columns into one
 */
function applyMerge(
  data: unknown[],
  sourceFields: string[],
  targetField: string,
  separator: string = ''
): unknown[] {
  return data.map(row => {
    const newRow = { ...(row as Record<string, unknown>) };
    const values = sourceFields.map(field => String(newRow[field] || ''));
    newRow[targetField] = values.join(separator);
    return newRow;
  });
}

type JsonRule = {
  op?: string;
  type?: string;
  from?: string;
  to?: string;
  field?: string;
  source?: string;
  target?: string;
  targetType?: string;
  toType?: string;
  search?: string;
  replace?: string;
  replacement?: string;
  value?: unknown;
};

function applyJsonRules(data: unknown[], config: Record<string, any>): unknown[] {
  const rawRules = config.rules ?? config.jsonRules ?? [];
  let rules: JsonRule[] = [];

  if (typeof rawRules === 'string') {
    const parsed = JSON.parse(rawRules);
    if (!Array.isArray(parsed)) {
      throw new Error('JSON_RULES rules must be array');
    }
    rules = parsed;
  } else if (Array.isArray(rawRules)) {
    rules = rawRules;
  } else {
    throw new Error('JSON_RULES requires rules');
  }

  let transformed = [...data];

  for (const rule of rules) {
    const op = String(rule.op || rule.type || '').trim().toLowerCase();
    switch (op) {
      case 'rename': {
        const from = String(rule.from || rule.source || '').trim();
        const to = String(rule.to || rule.target || '').trim();
        if (!from || !to) {
          throw new Error('JSON_RULES rename requires from/to');
        }
        transformed = transformed.map((record) => {
          const next = { ...(record as Record<string, unknown>) };
          const value = getValueByPath(next, from);
          deleteValueByPath(next, from);
          setValueByPath(next, to, value);
          return next;
        });
        break;
      }
      case 'cast': {
        const field = String(rule.field || '').trim();
        const targetType = String(rule.targetType || rule.toType || '').trim();
        if (!field || !targetType) {
          throw new Error('JSON_RULES cast requires field/targetType');
        }
        transformed = transformed.map((record) => {
          const next = { ...(record as Record<string, unknown>) };
          const value = getValueByPath(next, field);
          if (value !== null && value !== undefined) {
            setValueByPath(next, field, castValue(value, targetType));
          }
          return next;
        });
        break;
      }
      case 'trim': {
        const field = String(rule.field || '').trim();
        if (!field) {
          throw new Error('JSON_RULES trim requires field');
        }
        transformed = transformed.map((record) => {
          const next = { ...(record as Record<string, unknown>) };
          const value = getValueByPath(next, field);
          if (value !== null && value !== undefined) {
            setValueByPath(next, field, String(value).trim());
          }
          return next;
        });
        break;
      }
      case 'replace': {
        const field = String(rule.field || '').trim();
        const search = String(rule.search ?? '');
        const replaceWith = String(rule.replace ?? rule.replacement ?? '');
        if (!field || !search) {
          throw new Error('JSON_RULES replace requires field/search');
        }
        transformed = transformed.map((record) => {
          const next = { ...(record as Record<string, unknown>) };
          const value = getValueByPath(next, field);
          if (value !== null && value !== undefined) {
            setValueByPath(next, field, String(value).split(search).join(replaceWith));
          }
          return next;
        });
        break;
      }
      case 'default': {
        const field = String(rule.field || '').trim();
        if (!field) {
          throw new Error('JSON_RULES default requires field');
        }
        transformed = transformed.map((record) => {
          const next = { ...(record as Record<string, unknown>) };
          const value = getValueByPath(next, field);
          if (value === null || value === undefined || value === '') {
            setValueByPath(next, field, rule.value);
          }
          return next;
        });
        break;
      }
      case 'drop': {
        const field = String(rule.field || '').trim();
        if (!field) {
          throw new Error('JSON_RULES drop requires field');
        }
        transformed = transformed.map((record) => {
          const next = { ...(record as Record<string, unknown>) };
          deleteValueByPath(next, field);
          return next;
        });
        break;
      }
      case 'set': {
        const field = String(rule.field || '').trim();
        if (!field) {
          throw new Error('JSON_RULES set requires field');
        }
        transformed = transformed.map((record) => {
          const next = { ...(record as Record<string, unknown>) };
          setValueByPath(next, field, rule.value);
          return next;
        });
        break;
      }
      default:
        throw new Error(`JSON_RULES unsupported op: ${op || 'unknown'}`);
    }
  }

  return transformed;
}

function applyJavaScript(data: unknown[], config: Record<string, any>): unknown[] {
  const script = String(config.script || config.code || '').trim();
  if (!script) {
    throw new Error('JAVASCRIPT transformation requires script');
  }
  if (script.length > 8000) {
    throw new Error('JAVASCRIPT script exceeds max length (8000 chars)');
  }
  const blockedTokenPattern =
    /\b(require|process|global|globalThis|module|exports|Function|eval|import|child_process|fs|net|http|https|fetch|XMLHttpRequest|WebSocket)\b/;
  if (blockedTokenPattern.test(script)) {
    throw new Error('JAVASCRIPT script contains blocked token');
  }

  // Browser preview engine only: sandboxing is best-effort and must not be used for untrusted code execution.
  const transformFn = new Function(
    'row',
    'helpers',
    'index',
    `"use strict";\n${script}`
  ) as (row: Record<string, unknown>, helpers: Record<string, unknown>, index: number) => unknown;

  const helpers = {
    toNumber: (value: unknown, fallback = 0) => {
      const numeric = Number(value);
      return Number.isFinite(numeric) ? numeric : fallback;
    },
    toString: (value: unknown) => (value === null || value === undefined ? '' : String(value)),
    trim: (value: unknown) => (value === null || value === undefined ? '' : String(value).trim()),
    isEmpty: (value: unknown) => value === null || value === undefined || value === '',
  };

  const output = data
    .map((row, index) => transformFn({ ...(row as Record<string, unknown>) }, helpers, index))
    .filter((row) => row !== null && row !== undefined);

  const invalidRow = output.find((item) => item === null || Array.isArray(item) || typeof item !== 'object');
  if (invalidRow !== undefined) {
    throw new Error('JAVASCRIPT transformation must return object rows');
  }

  return output as unknown[];
}

function getPathTokens(path: string): string[] {
  return path.match(/[^.[\]]+/g) || [];
}

function getValueByPath(target: Record<string, unknown>, path: string): unknown {
  const tokens = getPathTokens(path);
  let current: unknown = target;

  for (const token of tokens) {
    if (current === null || current === undefined || typeof current !== 'object') {
      return undefined;
    }
    current = (current as Record<string, unknown>)[token];
  }
  return current;
}

function setValueByPath(target: Record<string, unknown>, path: string, value: unknown): void {
  const tokens = getPathTokens(path);
  if (tokens.length === 0) {
    return;
  }

  let current: Record<string, unknown> = target;
  for (let index = 0; index < tokens.length - 1; index += 1) {
    const token = tokens[index];
    const next = current[token];
    if (next === null || next === undefined || typeof next !== 'object') {
      current[token] = {};
    }
    current = current[token] as Record<string, unknown>;
  }

  current[tokens[tokens.length - 1]] = value;
}

function deleteValueByPath(target: Record<string, unknown>, path: string): void {
  const tokens = getPathTokens(path);
  if (tokens.length === 0) {
    return;
  }

  let current: Record<string, unknown> = target;
  for (let index = 0; index < tokens.length - 1; index += 1) {
    const token = tokens[index];
    const next = current[token];
    if (next === null || next === undefined || typeof next !== 'object') {
      return;
    }
    current = next as Record<string, unknown>;
  }

  delete current[tokens[tokens.length - 1]];
}

function castValue(value: unknown, targetType: string): unknown {
  switch (targetType.toUpperCase()) {
    case 'STRING':
      return String(value);
    case 'NUMBER':
      return Number(value);
    case 'BOOLEAN': {
      if (typeof value === 'string') {
        const normalized = value.toLowerCase().trim();
        if (normalized === 'false' || normalized === '0' || normalized === '') {
          return false;
        }
        if (normalized === 'true' || normalized === '1') {
          return true;
        }
      }
      return Boolean(value);
    }
    case 'DATE': {
      const date = new Date(value as any);
      return Number.isNaN(date.getTime()) ? value : date;
    }
    case 'JSON':
      if (typeof value === 'string') {
        try {
          return JSON.parse(value);
        } catch {
          return value;
        }
      }
      return value;
    default:
      return value;
  }
}
